
import { Component, input, signal, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import {
  ActionBarRight,
  AwActionBarComponent,
  AwDialogComponent,
  AwDividerComponent,
  AwSpinnerComponent,
  AwToastComponent,
  BreadCrumb,
  DialogFormControls,
  DialogOptions,
  ToastVariants,
  SingleSelectOption,
  AwBreadCrumbComponent,
} from '@assetworks-llc/aw-component-lib';

import { AwTextFormComponent } from '../../../../../shared/components/aw-text-form/aw-text-form.component';
import { AwDropdownFormComponent } from '../../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { Router } from '@angular/router';
import {
  CreateCustomerConfigurationRequest,
} from '../../../../../api/models';

import { CustomerService } from '../../../../../core/services/customer.service';
import { catchError, finalize, of } from 'rxjs';
import { AuthTypeValues } from '../../../../../types/types';
import { buildClientSecretName } from '../../../../../shared/utils/secrets.utils';

import { isValidOrganizationName, ORGANIZATION_NAME_MAX_LENGTH, ORGANIZATION_NAME_PATTERN, ORGANIZATION_NAME_PATTERN_MESSAGE } from '../../../../../shared/utils/organization-name';

import { UnsavedChangesForm } from '../../../../../shared/unsaved-changes/unsaved-changes-form';
import { UnsavedChangesDialogComponent } from '../../../../../shared/unsaved-changes/unsaved-changes-dialog.component';

@Component({
    selector: 'app-add-customer',
    imports: [
    AwDividerComponent,
    AwBreadCrumbComponent,
    AwTextFormComponent,
    AwSpinnerComponent,
    AwActionBarComponent,
    AwDialogComponent,
    AwDropdownFormComponent,
    AwToastComponent,
    UnsavedChangesDialogComponent,
],
    templateUrl: './add-customer.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './add-customer.component.scss',
        '../../../../../../styles/management.scss',
    ]
})
export class AddCustomerComponent extends UnsavedChangesForm {
  private readonly router = inject(Router);
  private readonly customerService = inject(CustomerService);

  readonly nameMaxLength = ORGANIZATION_NAME_MAX_LENGTH;
  readonly namePattern = ORGANIZATION_NAME_PATTERN;
  readonly namePatternMessage = ORGANIZATION_NAME_PATTERN_MESSAGE;

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isLoading: boolean = false;

  customerNameInput: string = '';
  customerStatusInput: string = '';
  customerStatusValue!: boolean;

  openClientInfoDialog: boolean = false;

  generatedSecret: string = '';
  generatedClientId: string = '';

  breadcrumbs = signal<BreadCrumb[]>([
    {
      label: 'Customer Management',
      route: '/customer-management/overview',
      newTab: false,
    },
    {
      label: 'New Customer',
      route: '/customer-management/add-customer',
      newTab: false,
    },
  ]);
  statusOptions = signal<SingleSelectOption[]>([
    {
      label: 'Active',
    },
    {
      label: 'Disabled',
    },
  ]);

  dialogOptions: DialogOptions = {
    variant: 'alert',
    title: 'Customer Info',
    description:
      'Keep track of this information. It will not be provided again after this.',
    primaryButtonLabel: 'Close',
  };
  formFieldsData: DialogFormControls = {
    controls: [],
  };
  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Cancel',
        buttonType: 'primary',
        action: () => this.router.navigate(['/customer-management/overview']),
      },
    },
    {
      buttonCallback: {
        label: 'Save',
        buttonType: 'outlined',
        action: () => this.createCustomer(),
      },
    },
  ]);

  onCustomerNameSelected(text: string | null) {
    this.customerNameInput = text || '';
  }
  onCustomerStatusSelected(status: SingleSelectOption | null) {
    this.customerStatusInput = status?.label || '';

    if (status?.label === 'Active') {
      this.customerStatusValue = true;
    } else if (status?.label === 'Disabled') {
      this.customerStatusValue = false;
    }
  }

  dialogCloseHandler() {
    this.openClientInfoDialog = false;
    this.router.navigate(['/customer-management/overview']).then(() => {
      window.location.reload();
    });
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  validateCustomerFields(): boolean {
    return (
      isValidOrganizationName(this.customerNameInput) &&
      this.customerStatusValue !== null &&
      this.customerStatusInput !== ''
    );
  }

  createCustomer(): void {
    if (!this.validateCustomerFields()) {
      this.buildToastMessage('Fill in all required customer fields.', 'error');
      return;
    }

    this.isLoading = true;

    const customerSecretName: string = buildClientSecretName();
    const createCustomerParams: CreateCustomerConfigurationRequest = {
      authenticationType: AuthTypeValues.OAUTH2,
      clientSecretName: customerSecretName,
      organizationName: this.customerNameInput,
      enabled: this.customerStatusValue,
    };

    this.customerService
      .createCustomer({ body: createCustomerParams })
      .pipe(
        catchError((error) => {
          this.buildToastMessage(
            'Failed to create customer. Please try again.',
            'error'
          );
          return of(null);
        }),
        finalize(() => {
          this.generatedClientId = '';
          this.generatedSecret = '';
          this.isLoading = false;
        })
      )
      .subscribe((newCustomer) => {
        if (!newCustomer) {
          return;
        }

        this.generatedClientId = newCustomer.clientId ?? '';
        this.markSaved();
        this.generatedSecret = newCustomer.clientSecret ?? '';
        this.formFieldsData = {
          controls: [
            {
              formControlName: 'secret_id',
              label: 'Client ID',
              value: this.generatedClientId,
              type: 'text',
              controlRequired: false,
              readOnly: true,
              fieldActions: [
                {
                  iconButtonCallback: {
                    action: () => this.copyToClipboard('' + newCustomer.clientId),
                    icon: "copy",
                    label: "Click to copy the client id to the clipboard",
                  }
                }
              ],
            },
            {
              formControlName: 'secret_name',
              label: 'Client Secret',
              value: this.generatedSecret,
              type: 'text',
              controlRequired: false,
              readOnly: true,
              fieldActions: [
                {
                  iconButtonCallback: {
                    action: () => this.copyToClipboard('' + newCustomer.clientSecret),
                    icon: "copy",
                    label: "Click to copy the client secret to the clipboard",
                  }
                }
              ],
            },

          ],
        };
        this.openClientInfoDialog = true;
        this.buildToastMessage('Successfully created customer.', 'success');
      });
  }
  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.buildToastMessage('Copied to clipboard', 'success');
    });
  }

  protected formState(): unknown {
    return { name: this.customerNameInput, status: this.customerStatusInput };
  }
}
